import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api from "../../lib/api";
import { useRealtime } from "../../hooks/useRealtime";
import {
  Hand, MessageSquare, Send, Inbox, History, ShieldCheck, AlertCircle,
  Check, X, ChevronRight,
} from "lucide-react";

/**
 * Module 2 — Delegate-facing Live Committee Room.
 *
 * Two strictly real-time features:
 *   • Digital Placards — raise / lower / see queue
 *   • Digital Chits   — send moderated peer-to-peer notes, see inbox
 *
 * Pure WebSocket sync via `useRealtime`. Mobile-first layout because most
 * delegates are using their phones during the conference floor.
 */
export default function CommitteeRoomPage() {
  const { eventId } = useParams();
  const [tab, setTab] = useState("placard"); // placard | send | inbox | sent

  const [placard, setPlacard] = useState(null);   // my placard
  const [queue, setQueue] = useState([]);          // visible queue
  const [roster, setRoster] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);

  const loadAll = () => {
    api.get(`/committee/placards/me?event_id=${eventId}`)
      .then((r) => setPlacard(r.data.placard)).catch(() => {});
    api.get(`/committee/placards/queue?event_id=${eventId}`)
      .then((r) => setQueue(r.data.queue || [])).catch(() => {});
    api.get(`/committee/roster?event_id=${eventId}`)
      .then((r) => setRoster(r.data.items || [])).catch(() => {});
    api.get(`/committee/chits/inbox?event_id=${eventId}`)
      .then((r) => setInbox(r.data.items || [])).catch(() => {});
    api.get(`/committee/chits/sent?event_id=${eventId}`)
      .then((r) => setSent(r.data.items || [])).catch(() => {});
  };
  useEffect(loadAll, [eventId]);

  // WS — placard queue updates for the whole event, plus my private chit channel
  const myId = JSON.parse(localStorage.getItem("user") || "{}").id;
  useRealtime(
    [`placards:${eventId}`, myId ? `chits:user:${myId}` : null].filter(Boolean),
    (msg) => {
      if (msg.kind?.startsWith("placard.")) {
        // Re-fetch the queue and my placard — cheap on a small event
        loadAll();
      }
      if (msg.kind?.startsWith("chit.")) {
        loadAll();
      }
    },
  );

  const raisePlacard = async (purpose) => {
    const r = await api.post("/committee/placards/raise",
      { event_id: eventId, purpose });
    setPlacard(r.data);
  };
  const lowerPlacard = async () => {
    if (!placard) return;
    await api.post("/committee/placards/lower",
      { placard_id: placard.id, action: "lower" });
    setPlacard(null);
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="committee-room-root">
        <header className="mb-4">
          <p className="text-[10px] uppercase tracking-[0.36em] text-[#D4AF37] font-bold flex items-center gap-2">
            <ShieldCheck className="h-3 w-3" /> Live Committee Room
          </p>
          <h1 className="mt-1 text-2xl font-bold text-[#0A192F]">
            Floor controls
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Raise your placard or send a chit. The chair recognises placards
            in the order they were raised; all chits are moderated before
            delivery.
          </p>
        </header>

        <Tabs current={tab} onChange={setTab} inboxCount={inbox.length} />

        {tab === "placard" && (
          <PlacardPanel
            placard={placard} queue={queue}
            onRaise={raisePlacard} onLower={lowerPlacard}
          />
        )}
        {tab === "send" && (
          <SendChitPanel roster={roster} eventId={eventId} onSent={loadAll} />
        )}
        {tab === "inbox" && <ChitInboxPanel inbox={inbox} />}
        {tab === "sent" && <ChitSentPanel sent={sent} />}
      </div>
    </DashboardLayout>
  );
}

function Tabs({ current, onChange, inboxCount }) {
  const tabs = [
    { id: "placard", label: "Placard", icon: Hand },
    { id: "send",    label: "Send Chit", icon: Send },
    { id: "inbox",   label: `Inbox${inboxCount ? ` (${inboxCount})` : ""}`, icon: Inbox },
    { id: "sent",    label: "Sent", icon: History },
  ];
  return (
    <div className="mb-5 flex gap-1 overflow-x-auto" data-testid="committee-tabs">
      {tabs.map((t) => {
        const active = current === t.id;
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            type="button" onClick={() => onChange(t.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap transition ${
              active ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
            data-testid={`committee-tab-${t.id}`}
          >
            <Icon className="h-3 w-3" />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Placard panel
// ---------------------------------------------------------------------------
function PlacardPanel({ placard, queue, onRaise, onLower }) {
  const [purpose, setPurpose] = useState("");
  const open = placard && ["raised", "recognised"].includes(placard.status);
  const myPosition = useMemo(() => {
    if (!open) return null;
    const i = queue.findIndex((q) => q.id === placard.id);
    return i >= 0 ? i + 1 : null;
  }, [open, placard, queue]);

  return (
    <div className="space-y-4">
      <section
        className={`rounded-2xl border-2 p-5 ${
          open ? "border-[#D4AF37] bg-[#FFFBEB]" : "border-slate-200 bg-white"
        }`}
        data-testid="placard-card"
      >
        {open ? (
          <>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
              Your placard is {placard.status === "recognised" ? "RECOGNISED" : "RAISED"}
            </p>
            <p className="mt-1 text-2xl font-bold text-[#0A192F]">
              {placard.status === "recognised"
                ? "🎤 The chair has recognised you — please address the floor."
                : `Position in queue: #${myPosition ?? "—"}`}
            </p>
            <p className="mt-2 text-xs text-slate-500" data-testid="placard-purpose">
              Purpose: {placard.purpose}
            </p>
            <button
              type="button" onClick={onLower}
              className="mt-4 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold"
              data-testid="placard-lower-btn"
            >
              Lower placard
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-700">
              Tap below to raise your placard. The chair sees a live FIFO
              queue and recognises delegates in the order they were raised.
            </p>
            <input
              type="text" value={purpose} onChange={(e) => setPurpose(e.target.value)}
              placeholder="Purpose (e.g. point of order, motion to extend GSL…)"
              className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              data-testid="placard-purpose-input"
              maxLength={160}
            />
            <button
              type="button" onClick={() => onRaise(purpose || undefined)}
              className="mt-3 w-full sm:w-auto px-5 py-3 rounded-md bg-[#D4AF37] text-[#0A192F] text-sm font-bold uppercase tracking-wider flex items-center gap-2 justify-center"
              data-testid="placard-raise-btn"
            >
              <Hand className="h-4 w-4" /> Raise placard
            </button>
          </>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="placard-queue">
        <h3 className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> Live queue ({queue.length})
        </h3>
        {queue.length === 0 && (
          <p className="mt-3 text-sm text-slate-400">
            No placards raised yet. Be the first to take the floor.
          </p>
        )}
        <ol className="mt-3 space-y-1.5">
          {queue.map((q, i) => (
            <li key={q.id}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs ${
                  q.status === "recognised" ? "bg-emerald-50 border border-emerald-200" : "bg-slate-50"
                }`}
                data-testid={`placard-queue-row-${i}`}>
              <span className="font-mono text-slate-500 w-6">#{i + 1}</span>
              <span className="font-semibold text-[#0A192F] flex-1 truncate">
                {q.delegate_country || q.delegate_name}
              </span>
              <span className="text-slate-500 truncate hidden sm:inline">{q.purpose}</span>
              {q.status === "recognised" && (
                <span className="text-[10px] uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded font-bold">
                  Recognised
                </span>
              )}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Send chit
// ---------------------------------------------------------------------------
function SendChitPanel({ roster, eventId, onSent }) {
  const [recipientId, setRecipientId] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const submit = async () => {
    if (!recipientId || !body.trim()) {
      setFeedback({ kind: "error", text: "Pick a recipient and write a note." });
      return;
    }
    setSending(true); setFeedback(null);
    try {
      await api.post("/committee/chits/send", {
        event_id: eventId, recipient_id: recipientId, body,
      });
      setFeedback({ kind: "ok", text: "Sent to chair for moderation." });
      setBody(""); setRecipientId("");
      onSent();
    } catch (e) {
      setFeedback({ kind: "error",
        text: e.response?.data?.detail || "Could not send." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="send-chit-card">
      <h3 className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">
        <MessageSquare className="h-4 w-4" /> Send a chit
      </h3>
      <p className="mt-1 text-xs text-slate-500">
        Notes are reviewed by the chair before delivery. Keep it diplomatic.
      </p>

      <label className="mt-4 block text-[10px] uppercase tracking-wider text-slate-500 font-bold">
        Recipient
      </label>
      <select
        value={recipientId} onChange={(e) => setRecipientId(e.target.value)}
        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
        data-testid="chit-recipient-select"
      >
        <option value="">— Select a delegate —</option>
        {roster.map((u) => (
          <option key={u.id} value={u.id}>
            {(u.country_assignment || u.country) ? `${u.country_assignment || u.country} · ` : ""}
            {u.name || u.full_name || u.email}
          </option>
        ))}
      </select>

      <label className="mt-3 block text-[10px] uppercase tracking-wider text-slate-500 font-bold">
        Message ({body.length}/600)
      </label>
      <textarea
        value={body} onChange={(e) => setBody(e.target.value.slice(0, 600))}
        rows={4} placeholder="Type your chit here…"
        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        data-testid="chit-body"
      />

      <button
        type="button" onClick={submit} disabled={sending}
        className="mt-3 px-4 py-2 rounded-md bg-[#0F4C81] text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50"
        data-testid="chit-send-btn"
      >
        <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Send to chair"}
      </button>

      {feedback && (
        <p className={`mt-3 text-xs ${feedback.kind === "ok" ? "text-emerald-700" : "text-rose-700"}`}
           data-testid="chit-feedback">
          {feedback.text}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Inbox + Sent
// ---------------------------------------------------------------------------
function ChitInboxPanel({ inbox }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="chit-inbox">
      <h3 className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">
        <Inbox className="h-4 w-4" /> Inbox ({inbox.length})
      </h3>
      {inbox.length === 0 && (
        <p className="mt-3 text-sm text-slate-400">No chits delivered yet.</p>
      )}
      <ul className="mt-3 divide-y divide-slate-100">
        {inbox.map((c) => (
          <li key={c.id} className="py-3" data-testid={`inbox-row-${c.id}`}>
            <p className="text-xs text-slate-500">
              From <strong className="text-[#0A192F]">{c.sender_name}</strong>{" "}
              · delivered {timeShort(c.moderated_at)}
            </p>
            <p className="mt-1 text-sm text-[#0A192F]">{c.body}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ChitSentPanel({ sent }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="chit-sent">
      <h3 className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">
        <History className="h-4 w-4" /> Sent ({sent.length})
      </h3>
      {sent.length === 0 && (
        <p className="mt-3 text-sm text-slate-400">You haven&apos;t sent any chits yet.</p>
      )}
      <ul className="mt-3 divide-y divide-slate-100">
        {sent.map((c) => (
          <li key={c.id} className="py-3" data-testid={`sent-row-${c.id}`}>
            <p className="text-xs text-slate-500 flex items-center gap-2">
              To <strong className="text-[#0A192F]">{c.recipient_name}</strong>
              <StatusPill status={c.status} />
              <span>· {timeShort(c.sent_at)}</span>
            </p>
            <p className="mt-1 text-sm text-[#0A192F]">{c.body}</p>
            {c.status === "rejected" && c.moderation_reason && (
              <p className="mt-1 text-[11px] text-rose-700 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> {c.moderation_reason}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:  "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-700",
    rejected: "bg-rose-100 text-rose-700",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold ${map[status] || "bg-slate-100 text-slate-600"}`}>
      {status}
    </span>
  );
}

function timeShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
